import React, { useState, useEffect } from "react";
import API from "../../api";
import toastr from "toastr";

interface TestTypeDetail {
  id: number;
  test_code: string;
  test_name: string;
  price: number | string;
  lower_limit?: string;
  upper_limit?: string;
  remarks?: string;
  method?: string;
  dept_id?: number;
  test_category?: number;
  category_name?: string;
  interpretation?: string;
  type_test: string;
  table_input?: string;
  units?: string;
  critical_info?: string;
  comments?: string;
  sub_head?: string;
  sample_type?: string;
  show_critical_info?: number;
  show_interpretation?: number;
}

interface Props {
  show: boolean;
  onClose: () => void;
  testId: number | null;
}

const ViewTestTypeModal: React.FC<Props> = ({ show, onClose, testId }) => {
  const [data, setData] = useState<TestTypeDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && testId) {
      fetchDetail(testId);
    } else {
      setData(null);
    }
  }, [show, testId]);

  const fetchDetail = async (id: number) => {
    setLoading(true);
    try {
      const res = await API.get(`testTypes/getTestTypeById/${id}`);
      setData(res.data.data);
    } catch {
      toastr.error("Failed to fetch test type details");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const isTableType = data?.type_test === "Table";

  const fieldStyle: React.CSSProperties = {
    background: "#111827",
    border: "1px solid #2d3748",
    borderRadius: "6px",
    padding: "8px 12px",
    color: "#e2e8f0",
    fontSize: "14px",
    minHeight: "38px",
    wordBreak: "break-word",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "4px",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const textareaStyle: React.CSSProperties = {
    ...fieldStyle,
    minHeight: "90px",
    whiteSpace: "pre-wrap",
    lineHeight: "1.5",
  };

  const Field = ({
    label,
    value,
    textarea = false,
  }: {
    label: string;
    value?: string | number | null;
    textarea?: boolean;
  }) => (
    <div style={{ marginBottom: "14px" }}>
      <div style={labelStyle}>{label}</div>
      <div style={textarea ? textareaStyle : fieldStyle}>
        {value !== null && value !== undefined && value !== ""
          ? String(value)
          : <span style={{ color: "#4b5563" }}>—</span>}
      </div>
    </div>
  );

  const BadgeField = ({
    label,
    value,
    active,
  }: {
    label: string;
    value: string;
    active: boolean;
  }) => (
    <div style={{ marginBottom: "14px" }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "2px" }}>
        <div style={fieldStyle}>{value}</div>
        <span
          style={{
            fontSize: "11px",
            padding: "2px 10px",
            borderRadius: "20px",
            background: active ? "rgba(34,197,94,0.15)" : "rgba(107,114,128,0.15)",
            color: active ? "#22c55e" : "#6b7280",
            border: `1px solid ${active ? "#22c55e44" : "#374151"}`,
            whiteSpace: "nowrap",
            fontWeight: 600,
          }}
        >
          {active ? "Show on Report" : "Hidden"}
        </span>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.75)",
          zIndex: 1040,
        }}
        onClick={onClose}
      />

      {/* Modal wrapper — scrollable */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1050,
          overflowY: "auto",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "24px 12px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "820px",
            background: "#1a1f2e",
            borderRadius: "12px",
            border: "1px solid #2d3748",
            boxShadow: "0 25px 60px rgba(0,0,0,0.7)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: "16px 22px",
              borderBottom: "1px solid #2d3748",
              background: "#111827",
              borderRadius: "12px 12px 0 0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "8px",
                  background: "rgba(99,102,241,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fa fa-flask" style={{ color: "#818cf8" }} />
              </div>
              <div>
                <h5 className="mb-0 text-white fw-semibold" style={{ fontSize: "16px" }}>
                  Test Type Details
                </h5>
                {data && (
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>
                    {data.test_code}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid #374151",
                borderRadius: "6px",
                color: "#9ca3af",
                width: "30px",
                height: "30px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: "22px" }}>
            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 0",
                  color: "#6b7280",
                }}
              >
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                />
                Loading details...
              </div>
            ) : data ? (
              <>
                {/* Section: Basic Info */}
                <div
                  style={{
                    fontSize: "11px",
                    color: "#4b5563",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: "12px",
                  }}
                >
                  Basic Information
                </div>

                <div className="row g-0" style={{ gap: 0 }}>
                  <div className="col-md-6" style={{ paddingRight: "12px" }}>
                    <Field label="Test Code" value={data.test_code} />
                    <Field label="Price" value={`₹${Number(data.price ?? 0).toFixed(2)}`} />
                    <Field label="Method" value={data.method} />
                    <Field label="Sample Type" value={data.sample_type} />
                    <Field label="Type of Test" value={data.type_test} />
                    <Field label="Units" value={data.units} />
                  </div>
                  <div className="col-md-6" style={{ paddingLeft: "12px" }}>
                    <Field label="Test Name" value={data.test_name} />
                    <Field label="Formula / Remarks" value={data.remarks} />
                    <Field label="Department" value={data.category_name} />
                    {isTableType ? (
                      <Field label="Table Input" value={data.table_input} textarea />
                    ) : (
                      <>
                        <Field label="Lower Limit" value={data.lower_limit} />
                        <Field label="Upper Limit" value={data.upper_limit} />
                        <Field label="Sub Heading" value={data.sub_head} />
                      </>
                    )}
                  </div>
                </div>

                <div
                  style={{
                    borderTop: "1px solid #1f2937",
                    margin: "8px 0 16px",
                  }}
                />

                {/* Critical Info */}
                <BadgeField
                  label="Critical Info"
                  value={data.critical_info || "—"}
                  active={!!data.show_critical_info}
                />

                <div
                  style={{
                    borderTop: "1px solid #1f2937",
                    margin: "8px 0 16px",
                  }}
                />

                {/* Interpretation */}
                <BadgeField
                  label="Interpretation"
                  value={data.interpretation || "—"}
                  active={!!data.show_interpretation}
                />

                {data.comments && (
                  <>
                    <div
                      style={{
                        borderTop: "1px solid #1f2937",
                        margin: "8px 0 16px",
                      }}
                    />
                    <Field label="Comments" value={data.comments} textarea />
                  </>
                )}
              </>
            ) : null}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "14px 22px",
              borderTop: "1px solid #2d3748",
              background: "#111827",
              borderRadius: "0 0 12px 12px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#1f2937",
                border: "1px solid #374151",
                borderRadius: "6px",
                color: "#d1d5db",
                padding: "7px 24px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewTestTypeModal;